/**
 * Sprint T-18 · Notification Studio · Template diff view.
 *
 * Minimal line-by-line diff (Q9 = a · no libs).
 * Colored:
 *   green  · line only in `b` (added)
 *   red    · line only in `a` (removed)
 *   grey   · unchanged (dimmed)
 *
 * The algorithm is intentionally basic (row-index alignment with padding),
 * suitable for short template overrides. For larger diffs the founder can
 * copy/paste elsewhere · out of scope for T-18.
 */
import React, { useMemo } from "react";

function toLines(s) {
  if (s == null) return [];
  return String(s).split("\n");
}

// tiny LCS-style alignment: build a merged sequence marking each row as
// 'same' | 'del' | 'add'. Uses classic dynamic programming over line sets.
function buildRows(a, b) {
  const A = toLines(a);
  const B = toLines(b);
  const m = A.length;
  const n = B.length;
  // dp[i][j] = LCS length of A[:i], B[:j]
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      if (A[i - 1] === B[j - 1]) dp[i][j] = dp[i - 1][j - 1] + 1;
      else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  // Walk backwards to build the diff sequence
  const rows = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (A[i - 1] === B[j - 1]) {
      rows.unshift({ kind: "same", text: A[i - 1] });
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      rows.unshift({ kind: "del", text: A[i - 1] });
      i -= 1;
    } else {
      rows.unshift({ kind: "add", text: B[j - 1] });
      j -= 1;
    }
  }
  while (i > 0) {
    rows.unshift({ kind: "del", text: A[i - 1] });
    i -= 1;
  }
  while (j > 0) {
    rows.unshift({ kind: "add", text: B[j - 1] });
    j -= 1;
  }
  return rows;
}

export default function TemplateDiffView({ baseText, overrideText, testid = "studio-template-diff" }) {
  const rows = useMemo(() => buildRows(baseText || "", overrideText || ""), [baseText, overrideText]);
  if (!baseText && !overrideText) {
    return (
      <div className="rounded border border-dashed border-[var(--ag-border)] p-3 text-center text-[10px] text-[var(--ag-text-dim)]">
        Nada que comparar.
      </div>
    );
  }
  const nAdds = rows.filter((r) => r.kind === "add").length;
  const nDels = rows.filter((r) => r.kind === "del").length;
  return (
    <div className="flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-3 text-[10px] text-[var(--ag-text-dim)]">
        <span className="text-emerald-300">+ {nAdds} adds</span>
        <span className="text-rose-300">- {nDels} dels</span>
        <span>· {rows.length} rows</span>
      </div>
      <pre className="max-h-[420px] overflow-auto rounded border border-[var(--ag-border)] bg-black/40 p-2 font-mono text-[10px] leading-relaxed">
        {rows.map((r, idx) => {
          const cls =
            r.kind === "add"
              ? "text-emerald-300 bg-emerald-500/10"
              : r.kind === "del"
              ? "text-rose-300 bg-rose-500/10"
              : "text-[var(--ag-text-dim)]";
          const prefix = r.kind === "add" ? "+ " : r.kind === "del" ? "- " : "  ";
          return (
            <div key={idx} className={cls}>
              {prefix}
              {r.text}
            </div>
          );
        })}
      </pre>
    </div>
  );
}
